import { isValidUUID } from "@semo/shared-utils";

/**
 * @spec-id CHT-018
 * @spec-layer DOMAIN
 * @spec-description ChannelNamingUtil domain model
 *
 * 🏷️ SEMO 채널 명명 규칙 통합 유틸리티
 * 간소화된 채널 네임스페이싱 전략 (user_id 기반):
 * - buyer와 supplier 간 1:1 격리
 * - Quote 기반 컨텍스트
 * - 단순하고 명확한 구조
 */
export const ChannelNamingUtil = {
  SEPARATOR: "_" as const,
  __VERSION: "v2" as const,

  /**
   * 기본 채팅방 채널 (간소화)
   * 형식: quote_{quoteId}_buyer_{buyerId}_supplier_{supplierId}
   *
   * @param roomId 채팅방 ID
   * @returns 표준화된 채널명
   */
  getChatRoomChannel(roomId: string): string {
    // 🔧 Simplified: organization_id 제거, roomId만 사용
    return `chat_room_${roomId}`;
  },

  /**
   * 견적 프로젝트 채널 (간소화)
   * @param quoteRequestId 견적 요청 ID
   * @returns 프로젝트별 채널명
   */
  getQuoteProjectChannel(quoteRequestId: string): string {
    // 🔧 Simplified: organization_id 제거
    return `quote_project_${quoteRequestId}`;
  },

  /**
   * 공급업체 전용 알림 채널 (간소화)
   * @param supplierId 공급업체 ID
   * @returns 공급업체 알림 채널명
   */
  getSupplierNotificationChannel(supplierId: string): string {
    // 🔧 Simplified: organization_id 제거
    return `supplier_${supplierId}_notifications`;
  },

  /**
   * 구매자 전용 알림 채널 (간소화)
   * @param buyerId 구매자 ID
   * @returns 구매자 알림 채널명
   */
  getBuyerNotificationChannel(buyerId: string): string {
    // 🔧 Simplified: organization_id 제거
    return `buyer_${buyerId}_notifications`;
  },

  /**
   * 관리자 채널 (간소화)
   * @param adminType 관리자 타입 (선택)
   * @returns 관리자 채널명
   */
  getAdminChannel(adminType = "general"): string {
    // 🔧 Simplified: organization_id 제거
    return `admin_${adminType}`;
  },

  /**
   * 채널명 파싱 (간소화된 형식)
   * @param channelName 전체 채널명
   * @returns 파싱된 채널 정보
   */
  parseChannelName(channelName: string): ChannelInfo {
    const parts = channelName.split(ChannelNamingUtil.SEPARATOR);

    // 🔧 Simplified format: type_resourceId
    // Examples:
    // - chat_room_{roomId}
    // - quote_project_{quoteId}
    // - buyer_{buyerId}_notifications
    // - supplier_{supplierId}_notifications

    if (parts.length < 2) {
      throw new Error("Invalid channel format - expected type_resourceId");
    }

    // 첫 번째 부분은 타입
    const type = parts[0];

    // 두 번째 부분은 리소스 ID
    let resourceId = parts[1];
    let subType: string | undefined;

    // notifications 등 서브타입이 있는 경우
    if (parts.length > 2 && parts[2] === "notifications") {
      subType = "notifications";
    }

    // chat_room이나 quote_project는 복합 타입
    if (type === "chat" && parts[1] === "room" && parts[2]) {
      resourceId = parts[2] ?? "";
    } else if (type === "quote" && parts[1] === "project" && parts[2]) {
      resourceId = parts[2] ?? "";
    }

    if (!type || !resourceId) {
      throw new Error("Invalid channel format - missing type or resourceId");
    }

    return {
      scope: "public", // 모든 채널을 public 스코프로 단순화
      // organizationId: // REMOVED: undefined, // organization_id 제거
      type: type as ChannelType,
      resourceId,
      subType,
    };
  },

  /**
   * 채널 유효성 검증 (간소화)
   * @param channelName 채널명
   * @returns 유효성 여부
   */
  isValidChannelName(channelName: string): boolean {
    if (!channelName || typeof channelName !== "string") {
      return false;
    }

    const parts = channelName.split(ChannelNamingUtil.SEPARATOR);

    // 🔧 Simplified: 최소 구조 type_resourceId (2개 부분)
    if (parts.length < 2) {
      return false;
    }

    const type = parts[0];

    // chat_room_{roomId} 또는 quote_project_{quoteId} 형식 확인
    if (type === "chat" && parts[1] === "room") {
      // chat_room_{roomId} 형식이어야 함
      if (parts.length < 3 || !parts[2] || parts[2].trim() === "") {
        return false;
      }
    } else if (type === "quote" && parts[1] === "project") {
      // quote_project_{quoteId} 형식이어야 함
      if (parts.length < 3 || !parts[2] || parts[2].trim() === "") {
        return false;
      }
    } else {
      // 일반 형식: type_{resourceId} (예: supplier_{id}_notifications)
      const resourceId = parts[1];
      if (
        !type ||
        type.trim() === "" ||
        !resourceId ||
        resourceId.trim() === ""
      ) {
        return false;
      }
    }

    return true;
  },

  /**
   * UUID 형식 검증
   * @private
   *
   * ✅ REFACTORED: Use centralized UUID validator (replaced inline regex)
   * Before: Inline UUID pattern /^[\da-f]{8}-...$/i (lines 165-166)
   * After: Import from @semo/shared-utils
   */
  isValidUUID(uuid: string): boolean {
    return isValidUUID(uuid);
  },
};

/**
 * 채널 정보 인터페이스
 */
export interface ChannelInfo {
  scope: "organization" | "public";
  type: ChannelType;
  resourceId: string;
  subType?: string;
}

/**
 * 채널 타입 정의
 */
export type ChannelType =
  | "chat-room"
  | "project"
  | "supplier"
  | "buyer"
  | "admin"
  | "notifications";

/**
 * 채널별 권한 매핑
 */
export const ChannelPermissionUtil = {
  /**
   * 사용자의 채널 접근 권한 확인 (간소화)
   */
  hasChannelAccess: async (
    channelInfo: ChannelInfo,
    _userAuthId: string,
    userRole: "buyer" | "supplier" | "admin",
  ): Promise<boolean> => {
    // 🔧 Simplified: organization 체크 제거
    // RLS 정책이 users와 chat_rooms 관계로 접근 제어를 담당

    // 채널 타입별 권한 확인
    return ChannelPermissionUtil.checkChannelTypePermission(
      channelInfo,
      userRole,
    );
  },

  checkChannelTypePermission: (
    channelInfo: ChannelInfo,
    userRole: "buyer" | "supplier" | "admin",
  ): boolean => {
    switch (channelInfo.type) {
      case "chat-room":
        return true; // 채팅방은 기본적으로 접근 가능
      case "admin":
        return userRole === "admin";
      case "supplier":
        return userRole === "supplier" || userRole === "admin";
      case "buyer":
        return userRole === "buyer" || userRole === "admin";
      default:
        return false;
    }
  },
};
